import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  createSaveTheDate,
  getUserSaveTheDate,
  updateSaveTheDate,
  uploadSaveTheDateCover,
  uploadSaveTheDateFooter,
} from "../../utils/saveTheDatesApi";
import { useAuth } from "../../contexts/AuthContext";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import "../Invitations/Invitations.css";
import "./SaveTheDates.css";

const API_BASE = process.env.REACT_APP_API_BASE;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const FALLBACK_TITLE = "Save The Date";

const buildDefaultTitle = (brideName, groomName, options = {}) => {
  const fallback =
    Object.prototype.hasOwnProperty.call(options, "fallback") &&
    options.fallback !== undefined
      ? options.fallback
      : FALLBACK_TITLE;
  const bride = brideName?.trim();
  const groom = groomName?.trim();
  if (bride && groom) {
    return `${bride} & ${groom}`;
  }
  if (bride) {
    return bride;
  }
  if (groom) {
    return groom;
  }
  return fallback;
};

const resolveImageUrl = (entity, key) =>
  entity?.[`${key}_image_url`] || entity?.[`${key}_url`] || "";

const SaveTheDateBuilder = () => {
  const navigate = useNavigate();
  const { saveTheDateId } = useParams();
  const isEdit = Boolean(saveTheDateId);
  const { wedding } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [existingItem, setExistingItem] = useState(null);

  const [form, setForm] = useState({
    title: "",
    location: "",
    message: "",
  });

  const [coverFile, setCoverFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [footerPreview, setFooterPreview] = useState("");
  const [coverError, setCoverError] = useState("");
  const [footerError, setFooterError] = useState("");

  const [profileTitle, setProfileTitle] = useState("");
  const [fetchedWeddingDate, setFetchedWeddingDate] = useState(null);
  const formRef = useRef(null);

  const defaultTitle = useMemo(
    () =>
      buildDefaultTitle(wedding?.bride_name, wedding?.groom_name, {
        fallback: "",
      }),
    [wedding]
  );

  const computedDefaultTitle = useMemo(() => {
    if (defaultTitle) return defaultTitle;
    if (profileTitle) return profileTitle;
    return FALLBACK_TITLE;
  }, [defaultTitle, profileTitle]);

  useEffect(() => {
    if (isEdit || profileTitle || defaultTitle) {
      return;
    }

    let active = true;
    const loadProfileNames = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/users/{user_id}`, {
          method: "GET",
        });
        if (!response || !response.ok) {
          return;
        }
        const data = await response.json();
        if (!active || !data) {
          return;
        }
        const combined = buildDefaultTitle(data.bride_name, data.groom_name, {
          fallback: "",
        });
        if (combined) {
          setProfileTitle(combined);
        }
      } catch (err) {
        console.warn("Unable to fetch user names for save-the-date title", err);
      }
    };

    loadProfileNames();
    return () => {
      active = false;
    };
  }, [isEdit, profileTitle, defaultTitle]);

  useEffect(() => {
    if (isEdit) {
      return;
    }

    if (wedding?.date) {
      setFetchedWeddingDate(wedding.date);
      return;
    }

    let cancelled = false;
    const fetchWeddingDate = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/weddings/my`, {
          method: "GET",
        });
        if (!response || !response.ok) {
          return;
        }
        const data = await response.json();
        if (cancelled) {
          return;
        }
        if (data?.date) {
          setFetchedWeddingDate(data.date);
        }
      } catch (err) {
        console.warn("Unable to fetch wedding date for save-the-date", err);
      }
    };

    fetchWeddingDate();
    return () => {
      cancelled = true;
    };
  }, [isEdit, wedding]);

  useEffect(() => {
    if (isEdit || !computedDefaultTitle) {
      return;
    }
    if (form.title === computedDefaultTitle) {
      return;
    }
    const shouldOverwrite = !form.title || form.title === FALLBACK_TITLE;
    if (!shouldOverwrite) {
      return;
    }
    setForm((prev) => ({ ...prev, title: computedDefaultTitle }));
  }, [isEdit, computedDefaultTitle, form.title]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!isEdit || !saveTheDateId) {
          setLoading(false);
          return;
        }

        const saveTheDate = await getUserSaveTheDate(saveTheDateId);
        setExistingItem(saveTheDate);
        setForm({
          title: saveTheDate.title || "",
          location: saveTheDate.location || saveTheDate.venue_name || "",
          message: saveTheDate.message || "",
        });
      } catch (err) {
        console.error(err);
        setError(err.message || "Настана грешка при вчитување на Save the Date.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isEdit, saveTheDateId]);

  useEffect(() => {
    if (!coverFile) {
      setCoverPreview(resolveImageUrl(existingItem, "cover"));
      return;
    }
    const objectUrl = URL.createObjectURL(coverFile);
    setCoverPreview(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [coverFile, existingItem]);

  useEffect(() => {
    if (!footerFile) {
      setFooterPreview(resolveImageUrl(existingItem, "footer"));
      return;
    }
    const objectUrl = URL.createObjectURL(footerFile);
    setFooterPreview(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [footerFile, existingItem]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const validateFile = (file, setErrorState) => {
    if (!file) {
      return false;
    }
    const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      setErrorState("Дозволени формати се: png, jpg, jpeg.");
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrorState("Максималната големина за датотеката е 10MB.");
      return false;
    }
    setErrorState("");
    return true;
  };

  const handleFileChange = (type) => (event) => {
    const file = event.target.files?.[0] || null;
    if (event.target) {
      event.target.value = "";
    }
    if (!file) {
      return;
    }

    if (type === "cover") {
      if (validateFile(file, setCoverError)) {
        setCoverFile(file);
      }
      return;
    }

    if (validateFile(file, setFooterError)) {
      setFooterFile(file);
    }
  };

  const uploadImagesIfNeeded = async (id) => {
    if (!id) {
      return;
    }
    if (coverFile) {
      const uploadedCover = await uploadSaveTheDateCover(id, coverFile);
      setExistingItem((prev) =>
        prev
          ? {
              ...prev,
              cover_image_url:
                uploadedCover?.cover_image_url ||
                uploadedCover?.cover_url ||
                prev.cover_image_url,
            }
          : prev
      );
      setCoverFile(null);
    }
    if (footerFile) {
      const uploadedFooter = await uploadSaveTheDateFooter(id, footerFile);
      setExistingItem((prev) =>
        prev
          ? {
              ...prev,
              footer_image_url:
                uploadedFooter?.footer_image_url ||
                uploadedFooter?.footer_url ||
                prev.footer_image_url,
            }
          : prev
      );
      setFooterFile(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    const resolvedWeddingDate = wedding?.date || fetchedWeddingDate || null;
    const eventDateIso = resolvedWeddingDate
      ? new Date(resolvedWeddingDate).toISOString()
      : null;
    const titleValue = form.title.trim() || computedDefaultTitle;

    if (!eventDateIso) {
      setError("За Save the Date прво внесете датум на свадба во Профил.");
      setSubmitting(false);
      return;
    }

    try {
      if (isEdit && saveTheDateId) {
        const payload = {
          title: titleValue,
          event_date: eventDateIso,
          location: form.location.trim() || null,
          message: form.message.trim() || null,
          is_public: true,
        };
        const result = await updateSaveTheDate(saveTheDateId, payload);
        setExistingItem((prev) => ({ ...(prev || {}), ...(result || {}) }));
        await uploadImagesIfNeeded(saveTheDateId);
        setSuccessMessage({
          text: "Save the Date е ажуриран.",
          slug: result?.slug || existingItem?.slug,
        });
      } else {
        if (!coverFile || !footerFile) {
          setError("Изберете и насловна и долна фотографија.");
          setSubmitting(false);
          return;
        }

        const payload = {
          title: titleValue,
          event_date: eventDateIso,
          location: form.location.trim() || null,
          message: form.message.trim() || null,
          is_public: true,
          cover_file: coverFile,
          footer_file: footerFile,
        };

        const result = await createSaveTheDate(payload);
        setExistingItem(result);
        setSuccessMessage({
          text: "Save the Date е креиран.",
          slug: result?.slug,
        });
        setCoverFile(null);
        setFooterFile(null);
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setError(
        err.message ||
          "Настана грешка при зачувување на Save the Date. Обидете се повторно."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box className="invitation-page-container">
        <CircularProgress />
        <Typography mt={2}>Вчитување...</Typography>
      </Box>
    );
  }

  const headline = isEdit ? "Уреди Save The Date" : "Креирај Save The Date";
  const subHeadline = isEdit
    ? "Ажурирајте ги деталите и фотографиите."
    : "Внесете основни информации и прикачете 2 фотографии.";
  const resolvedWeddingDate = wedding?.date || fetchedWeddingDate || null;
  const weddingDateLabel = resolvedWeddingDate
    ? new Date(resolvedWeddingDate).toLocaleString("mk-MK", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <Box className="invitation-page-container">
      <Stack spacing={2} alignItems="center" className="invitation-header">
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: "2.4rem", sm: "2.6rem", md: "2.8rem" } }}
        >
          {headline}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {subHeadline}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="text"
            onClick={() => navigate("/save-the-dates")}
            sx={buttonSx}
          >
            ← Назад
          </Button>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={() => formRef.current?.requestSubmit()}
            sx={{
              backgroundColor: "var(--primary-color)",
              "&:hover": { backgroundColor: "var(--primary-color-dark)" },
            }}
          >
            {submitting
              ? "Се зачувува..."
              : isEdit
              ? "Зачувај измени"
              : "Креирај Save The Date"}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Alert severity={resolvedWeddingDate ? "info" : "warning"} sx={{ mb: 2 }}>
        {resolvedWeddingDate ? (
          <>
            Датум за Save the Date: <strong>{weddingDateLabel}</strong>. Се менува само во{" "}
            <Button
              component={RouterLink}
              to="/profile"
              size="small"
              sx={{ p: 0, minWidth: "auto", verticalAlign: "baseline" }}
            >
              Профил
            </Button>
            .
          </>
        ) : (
          <>
            Немате внесен датум на свадба. Внесете го во{" "}
            <Button
              component={RouterLink}
              to="/profile"
              size="small"
              sx={{ p: 0, minWidth: "auto", verticalAlign: "baseline" }}
            >
              Профил
            </Button>{" "}
            за да зачувате Save the Date.
          </>
        )}
      </Alert>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
            <span>{successMessage.text}</span>
            {successMessage.slug && (
              <Button
                size="small"
                component="a"
                href={`/save-the-dates/${successMessage.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={buttonSx}
              >
                Отвори линк
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      <Card className="invitation-card form-card">
        <CardContent>
          <Box component="form" ref={formRef} onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Наслов"
                  value={form.title}
                  onChange={handleChange("title")}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Локација"
                  placeholder="Ресторан / сала / venue"
                  value={form.location}
                  onChange={handleChange("location")}
                />
              </Grid>
              {/* Save the Date is always public for now. */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Порака"
                  value={form.message}
                  onChange={handleChange("message")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    Насловна слика {isEdit ? "(опционално)" : "(задолжително)"}
                  </Typography>
                  <Button component="label" variant="outlined" sx={buttonSx}>
                    Избери слика
                    <input
                      hidden
                      type="file"
                      accept=".png,.jpg,.jpeg,image/png,image/jpg,image/jpeg"
                      onChange={handleFileChange("cover")}
                    />
                  </Button>
                  {coverError && <Alert severity="error">{coverError}</Alert>}
                  {coverPreview && (
                    <img
                      src={coverPreview}
                      alt="Cover preview"
                      className="std-upload-preview"
                    />
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    Долна слика {isEdit ? "(опционално)" : "(задолжително)"}
                  </Typography>
                  <Button component="label" variant="outlined" sx={buttonSx}>
                    Избери слика
                    <input
                      hidden
                      type="file"
                      accept=".png,.jpg,.jpeg,image/png,image/jpg,image/jpeg"
                      onChange={handleFileChange("footer")}
                    />
                  </Button>
                  {footerError && <Alert severity="error">{footerError}</Alert>}
                  {footerPreview && (
                    <img
                      src={footerPreview}
                      alt="Footer preview"
                      className="std-upload-preview"
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const buttonSx = {
  color: "var(--primary-color)",
  "&:hover": {
    backgroundColor: "rgba(227, 95, 135, 0.08)",
  },
};

export default SaveTheDateBuilder;
